import { useState, useEffect, useCallback, useMemo } from "react";
import {useDispatch, useSelector} from 'react-redux';
import { setCurrentResultsElement } from "../../../../actions/UserActions";
import { getPageElements } from "../../../utils/PageElements";
import Dropdown from "../../../utils/Dropdown";
import moment from 'moment';
import {
	getNumericPredictionResults,
	getPollResults,
	getRegistrationResults,
	getScratchResults,
	getSoccerLineUpResults,
	getWheelOfPrizesResults,
	getQuizWinnerStats,
	getFreeTextResults,
} from './ResultsCreator';
import { saveCSV } from "../../../utils/csv.mjs";
import { saveExcel, styledCell } from "../../../utils/excel.mjs";
import { SwiperSlide } from 'swiper/react';
import HorizontalSlider from "../../../utils/HorizontalSlider";

const removeHtmlTagsAndSpecialChars = (str) => {
	str = str.replace(/<\/?[^>]+(>|$)/g, "");
	str = str.replace(/&nbsp;/g, " ");
	str = str.replace(/&amp;/g, "&");
	str = str.replace(/&lt;/g, "<");
	str = str.replace(/&gt;/g, ">");
	return str;
}
const toMillis = (timestamp)=>timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);

const CORRECT_KEY = Symbol("correct data");
class Element {
	#canDetailed;
	#title;
	#isInput;
	#hasDetailedGraphs;
	#detailedSubHeaders;
	#optionsMap;
	#correctnessFn;
	constructor(config, pageId, order){
		this.id = config.id;
		this.order = order;
		this.pageId = pageId;
		this.config = config;
	}
	getTitle() {
		if(this.#title===undefined){
			switch(this.config.type){
				case "poll":
				case "multipleChoice":
				case "imageChoice":
				case "mvp": {
					if(typeof this.config.question === "string") this.#title = removeHtmlTagsAndSpecialChars(this.config.question);
					break;
				}
				case "registrationForm":{
					if(typeof this.config.header === "string") this.#title = removeHtmlTagsAndSpecialChars(this.config.header);
					break;
				}
				case "numericPrediction":{
					if(typeof this.config.header?.html === "string") this.#title = removeHtmlTagsAndSpecialChars(this.config.header.html);
					break;
				}
				case "freeText":{
					this.#title = "Free Text";
					break;
				}
			}
			// If the title was not explicitly set or is empty, try the default text
			if(!this.#title) this.#title = getPageElements(this.config.type)?.text;
			// Fallback
			if(!this.#title) this.#title = this.config.type;
		}
		return this.#title;
	}
	isInput() {
		if(this.#isInput===undefined){
			this.#isInput = getPageElements(this.config.type)?.isInput??null;
		}
		return this.#isInput;
	}
	optionsMap() {
		if(this.#optionsMap===undefined){
			const m = new Map();
			this.config.options.forEach((o, i)=>m.set(o.id, {"text": o.text, "isCorrect": o.isCorrect}));
			this.#optionsMap = m;
		}
		return this.#optionsMap;
	}
	hasDetailedGraphs() {
		if(this.#hasDetailedGraphs===undefined){
			this.#hasDetailedGraphs = this.isInput();
			if (this.config.type === "soccerLineupSelector"){
				// Not implemented yet
				this.#hasDetailedGraphs = false;
			}
		}
		return this.#hasDetailedGraphs;
	}
	isCorrect(selectedOptions){
		if(this.#correctnessFn===undefined){
			const correctOptions = this.config.options.filter(o=>o.isCorrect).map(o=>o.id);
			if(this.config.criteria === "all"){
				this.#correctnessFn = (sOpts) =>{
					if(correctOptions.length!=sOpts.length) return false;
					for (let i = correctOptions.length - 1; i >= 0; i--) {
						if(!sOpts.includes(correctOptions[i])) return false;
					}
					return true;
				}
			}else{
				this.#correctnessFn = (sOpts) =>{
					let allSelectedAreCorrect = false;
					if(sOpts.length){
						allSelectedAreCorrect = true;
						for (let i = sOpts.length - 1; i >= 0; i--) {
							if(!correctOptions.includes(sOpts[i])) allSelectedAreCorrect = false;
						}
					}
					return allSelectedAreCorrect;
				}
			}
		}
		return this.#correctnessFn(selectedOptions);
	}
	getDetailedSubheaders() {
		if(this.#detailedSubHeaders===undefined){
			if(!this.hasDetailedGraphs()){
				this.#detailedSubHeaders = [];
			}else{
				switch(this.config.type){
					case "registrationForm": {
						this.#detailedSubHeaders = this.config.fields.map(f=>({"title":(f.type==="checkbox")?removeHtmlTagsAndSpecialChars(f.label):f.label, "id":f.id}));
						break;
					}
					case "imageChoice":
					case "numericPrediction":
					case "mvp":
					case "poll": {
						if(this.config.max<=1) this.#detailedSubHeaders = [{"title":"", "id":null}]
						else this.#detailedSubHeaders = this.config.options.map(o=>({"title":o.text, "id":o.id}));
						break;
					}
					case "multipleChoice":{
						if(this.config.max<=1) this.#detailedSubHeaders = [{"title":"", "id":null}]
						else this.#detailedSubHeaders = this.config.options.map(o=>({"title":o.text, "id":o.id}));
						break;
					}
					case "wheelOfPrizes":
					case "scratch": {
						this.#detailedSubHeaders = [{"title":"", "id":null}];
						break;
					}
					default: {
						console.log("Element", this.config);
						throw new Error("Not implemented for: " + this.config.type);
					}
				}
			}
		}
		return this.#detailedSubHeaders;
	}
	genUserSegment(userAnswers) {
		const subHeaders = this.getDetailedSubheaders();
		if(!subHeaders.length) return [];
		const elementAnswer = userAnswers?.[this.pageId]?.elements?.[this.id];
		switch(this.config.type){
			case "registrationForm": {
				const segment = subHeaders.map(({id})=>{
					let tmp = elementAnswer?.value[id]??"";
					if(Array.isArray(tmp)) tmp = tmp.join(", ");
					else if(tmp===true) tmp="Yes";
					else if(tmp===false) tmp="No";
					return tmp;
				});
				return segment;
			}
			case "numericPrediction":{
				const segment = subHeaders.map(({id})=>elementAnswer?.value?.[id]??"-");
				return segment;
			}
			case "imageChoice":
			case "mvp":
			case "poll": {
				const uv = elementAnswer?.value??[];
				if(this.config.max<=1) {
					const m = this.optionsMap();
					let v = "";
					// Only one option should be selected, but joining just in case
					v = uv.map((optionId)=>{
						const tmp = m.get(optionId);
						if(!tmp) return "Unknown option";
						if(tmp.text) return tmp.text;
					}).join(", ");
					return [v];
				} else {
					const segment = subHeaders.map(({id})=>uv.includes(id)?"Yes":"No");
					return segment;
				}
			}
			case "multipleChoice":{
				const uv = elementAnswer?.value??[];
				const isCorrect = this.isCorrect(uv);
				let segment;
				if(this.config.max<=1) {
					const m = this.optionsMap();
					let v = "";
					// Only one option should be selected, but joining just in case
					v = uv.map((optionId)=>{
						const tmp = m.get(optionId);
						if(!tmp) return "Unknown option";
						if(tmp.text) return tmp.text;
					}).join(", ");
					segment = [v];
				} else {
					segment = subHeaders.map(({id})=>{
						return uv.includes(id)?"Yes":"No"
					});
				}
				segment[CORRECT_KEY] = {
					hasCorrect: true,
					isCorrect: isCorrect,
				}
				return segment;
			}
			case "wheelOfPrizes":
			case "scratch": {
				const v = elementAnswer?.value;
				return [v?v.name:"-"];
			}
			default: {
				console.log("Element", this.config);
				throw new Error("Not implemented for: " + this.config.type);
			}
		}
	}
	getTimestamp(userAnswers) {
		return userAnswers?.[this.pageId]?.elements?.[this.id]?.timestamp;
	}
}

function ResultsList() {
	const dispatch = useDispatch();
	const {resultsCurrentElement, unMappedUserResults, currentCampaign, currentCampaignId } = useSelector(state => state.user);
	const [sidebarItems, setSidebarItems] = useState([])
	const [forDownload, setForDownload] = useState([])

	let hasCorrectOption = false;
	for (var i = 0; !hasCorrectOption && i < currentCampaign.pages.length; i++) {
		const page = currentCampaign.pages[i];
		for (var j = 0; !hasCorrectOption && j < page.elements.length; j++) {
			const element = page.elements[j];
			if(element.type=="multipleChoice"){
				for (var k = 0; !hasCorrectOption && k < element.options.length; k++) {
					const option = element.options[k];
					if(option.isCorrect) hasCorrectOption = true;
				}
			}
		}
	}

	const mapQuestions = useCallback(() => {
		/* When other type of campaigns are added we have to take into consideration the campaign type in order to create the sidebar */
		let tableHeaderRegistration = [];
		let o = [];
		let registrationPlaceholders = {}
		currentCampaign.pages.forEach( page => {
			page.elements.forEach( element => {
				const defaultElement = getPageElements(element.type)
				let title = defaultElement?.text || 'Element';
				if(element.hasOwnProperty('header')){
					if (typeof element.header == 'object') title = removeHtmlTagsAndSpecialChars(element.header.html)
				}
				if (element.hasOwnProperty('question')) {
					title = removeHtmlTagsAndSpecialChars(element.question)
				}
				let tmp = {
					id: element.id,
					type: element.type,
					icon: defaultElement?.icon,
					title: title,
					tableHeader: [],
					rowPlaceholder: {},
					generalReportPlaceholder : {},
					answers: []
				};
				if(element.type === 'numericPrediction'){
					element.options.forEach( (option, idx) => {
						tmp.tableHeader.push({key: option.id, type: 'options', value: option.text ? option.text : 'Option '+ idx+1});
						tmp.rowPlaceholder[option.id] = null;
						tmp.generalReportPlaceholder[option.id] = [];

					})
					o.push(tmp)
				}
				if(element.type === 'registrationForm'){
					element.fields.forEach( field => {
						tableHeaderRegistration.push({key: field.id, type: `registration_${field.type}`, value: field.label});
						registrationPlaceholders[field.id] = null
					})
					o.push(tmp)
				}
				if (element.type === 'poll') {
					element.options.forEach((option, idx) => {
						//tmp.tableHeader.push({ key: option.id, type: 'options', value: option.text ? option.text : 'Option ' + idx + 1 });
						tmp.tableHeader = [{ key: option.id, type: 'options', value: 'Selected' }];
						tmp.rowPlaceholder[option.id] = null;
						tmp.generalReportPlaceholder[option.id] = {
							'text': option.text,
							'percentage': 0,
							'answers': []
						};
					})
					o.push(tmp)
				}
				if (element.type === 'multipleChoice') {
					element.options.forEach((option, idx) => {
						//tmp.tableHeader.push({ key: option.id, type: 'options', value: option.text ? option.text : 'Option ' + idx + 1 });
						tmp.tableHeader = [{ key: option.id, type: 'options', value: 'Selected' }];
						tmp.rowPlaceholder[option.id] = null;
						tmp.generalReportPlaceholder[option.id] = {
							'text': option.text,
							'percentage': 0,
							'isCorrect': !!option.isCorrect,
							'answers': []
						};
					})
					o.push(tmp)
				}
				if (element.type === 'imageChoice') {
					element.options.forEach((option, idx) => {
						//tmp.tableHeader.push({ key: option.id, type: 'options', value: option.text ? option.text : 'Option ' + idx + 1 });
						tmp.tableHeader = [{ key: option.id, type: 'options', value: 'Selected'}];
						tmp.rowPlaceholder[option.id] = null;
						tmp.generalReportPlaceholder[option.id] = {
							'text': option.text,
							'percentage': 0,
							'image': option.image,
							'answers': []
						};
					})
					o.push(tmp)
				}
				if (element.type === 'mvp') {
					element.options.forEach((option, idx) => {
						//tmp.tableHeader.push({ key: option.id, type: 'options', value: option.text ? option.text : 'Option ' + idx + 1 });
						tmp.tableHeader = [{ key: option.id, type: 'options', value: 'Selected' }];
						tmp.rowPlaceholder[option.id] = null;
						tmp.generalReportPlaceholder[option.id] = {
							'text': option.text,
							'percentage': 0,
							'image': option.image,
							'answers': []
						};
					})
					o.push(tmp)
				}
				if (element.type === 'soccerLineupSelector') {
					tmp.generalReportPlaceholder = {
						formation: element.formations[0],
						categories: element.categories,
						players: element.players,
					};
					tmp.tableHeader.push({ key: 'asd', type: 'timeadded', value: 'Players' });
					tmp.rowPlaceholder['players'] = ""
					o.push(tmp)
				}
				if (element.type === 'wheelOfPrizes') {
					element.slices.forEach((slice) => {
						slice.prizes.forEach((prize) => {
							tmp.rowPlaceholder[prize.id] = 'No prize'
							tmp.generalReportPlaceholder[prize.id] = {...prize, prizeCount: 0};
						})
					})
					tmp.tableHeader.push({ key: 'prize', type: 'prize', value: 'Prize' })
					o.push(tmp)
				}
				if (element.type === 'scratch') {
					element.prizes.forEach((prize) => {
						tmp.rowPlaceholder[prize.id] = 'No prize'
						tmp.generalReportPlaceholder[prize.id] = {...prize, prizeCount: 0};
					})
					tmp.tableHeader.push({ key: 'prize', type: 'prize', value: 'Prize' })
					o.push(tmp)
				}
				if(element.type === 'freeText'){
					tmp.title = "Free text";
					tmp.tableHeader.push({key: element.id, type: 'text', value: "Text"});
					tmp.rowPlaceholder[element.id] = null;
					tmp.generalReportPlaceholder[element.id] = [];
					o.push(tmp)
				}
			})
		})
		o.forEach( item => {
			item.tableHeader = [...tableHeaderRegistration, ...item.tableHeader];
			item.tableHeader.push({ key: 'timeAdded', type:'timeadded', value: 'Time Added'});
			item.rowPlaceholder = {...registrationPlaceholders, ...item.rowPlaceholder, ...{timeAdded: null}}
		})
		if(o.length > 0) dispatch(setCurrentResultsElement(o[0]))
		setSidebarItems(o)
	}, [currentCampaign.pages, dispatch]);

	const isLorealHack = 
		window.location.pathname.includes("rbS2JkgfXoFpOzUDOpAB") || // debug id
		window.location.pathname.includes("9zMyWeDeD19cbYV52OLb") || // LOREAL haircare
		window.location.pathname.includes("wMXOHi5JrXHqY3OwgS2x") || // LOREAL skincare
		window.location.pathname.includes("ohsQBNUi8vu05L98amEI"); // LOREAL makeup

	const winnerStats = useMemo(() => {
		if(!isLorealHack) return [0, 0];

		if(sidebarItems?.length > 0 && Object.keys(unMappedUserResults).length > 0){
			// return [0, 0]
			return getQuizWinnerStats(unMappedUserResults, sidebarItems);
		}
		else{
			return [0, 0]
		}
	}, [isLorealHack, sidebarItems, unMappedUserResults])

	const elementsMap = useMemo(()=>{
		const map = new Map();
		let i = 0;
		currentCampaign.pages.forEach(page => {
			page.elements.forEach(element => {
				map.set(element.id, new Element(element, page.id, i));
				i++;
			});
		});
		return map;
	}, [currentCampaign.pages]);

	const getFullDetailedReport = () =>{
		const detailedElements = [];
		elementsMap.forEach(e=>{
			if(e.hasDetailedGraphs()) detailedElements.push(e);
		});
		detailedElements.sort((a,b)=>a.order-b.order);
		const quizQuestionCount = detailedElements.reduce((acc, e)=>{
			if(e.config.type==="multipleChoice") return acc+1;
			return acc;
		}, 0);

		const header = detailedElements.flatMap((element)=>{
			const t = element.getTitle();
			return element.getDetailedSubheaders().map((_,i)=>i===0?t:"");
		});
		const subheader = detailedElements.flatMap((element)=>element.getDetailedSubheaders().map(h=>h.title));
		header.push("Correct Answers");
		if(quizQuestionCount>0) header.push("Last answer time");
		subheader.push("");
		const timestampIndex = header.length-1;
		let table = [
			header,
			subheader,
		];
		for(const userId in unMappedUserResults){
			const userAnswers = unMappedUserResults[userId];
			let maxAnswerTime = null;
			let correctCount = 0;
			const userRow = detailedElements.flatMap((element)=>{
				const t = element.getTimestamp(userAnswers);
				if(t){
					if(!maxAnswerTime) maxAnswerTime = toMillis(t);
					maxAnswerTime = Math.max(maxAnswerTime, toMillis(t));
				}
				const seg = element.genUserSegment(userAnswers);
				if(seg[CORRECT_KEY]?.hasCorrect){
					if(seg[CORRECT_KEY].isCorrect){
						correctCount++;
						for (let i = seg.length - 1; i >= 0; i--) {
							seg[i] = styledCell(seg[i], { fill: { patternType: 'solid', fgColor: { rgb: 'C6EFCE' } } });
						}
					}
				}
				return seg;
			});
			if(quizQuestionCount>0) userRow.push(`${correctCount}/quizQuestionCount`);
			userRow.push(maxAnswerTime?new Date(maxAnswerTime):"");
			table.push(userRow);
		}

		const meta = [];
		meta[timestampIndex]={width:19};
		saveExcel(table, "Detailed report", meta, (cell, pos)=>{
			if(pos.row<=1){
				cell.s = cell.s || {};
				cell.s.font = { ...(cell.s.font || {}), bold: true };
			}else if(pos.col==(timestampIndex)){
				if(cell.t==="d") cell.z = 'yyyy-mm-dd hh:mm:ss';
			}
		})

	}

	const getCss = (id) => {
		let o = 'tabs__item results__item';
		if(resultsCurrentElement){
			if(resultsCurrentElement.id === id) o += ' tabs__item-active';
		}
		return o;
	}
	const prepareDataForDownload = useCallback((elements) => {
		const endDate = currentCampaign.endDate ? moment(currentCampaign.endDate).format("YYYY-MM-DD HH:mm") : ''
		const d = elements.map(async (element) => {
			let d = []
			switch (element.type) {
				case 'numericPrediction':
					await getNumericPredictionResults(unMappedUserResults, element, 1)
						.then((calculatedResults) => {
							const c = calculatedResults[0].map((row) => {
								const r = row.map((value) => {
									return value
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'registrationForm':
					await getRegistrationResults(unMappedUserResults, element.rowPlaceholder)
						.then((calculatedResults) => {
							const c = calculatedResults.map((row) => {
								const r = row.map((value) => {
									return value
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'poll':
				case 'multipleChoice':
				case 'imageChoice':
				case 'mvp':
					await getPollResults(unMappedUserResults, element)
						.then( pollResults => {
							const [_, detailedAnswersByUser] = pollResults;
							const c = detailedAnswersByUser.map((row) => {
								const r = Object.values(row).map((value) => {
									return value
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'soccerLineupSelector':
					await getSoccerLineUpResults(unMappedUserResults, element)
						.then((calculatedResults) => {
							const rows = calculatedResults[3];
							if(!rows) return;
							const c = rows.map((r) => {
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...Object.values(r)]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'wheelOfPrizes':
					await getWheelOfPrizesResults(unMappedUserResults, element, false)
						.then((calculatedResults) => {
							const c = calculatedResults[0].map((row) => {
								const r = row.map((value) => {
									if(value && typeof value === 'object'){
										return Array.isArray(value) ? value : value.text
									}
									return value
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'scratch':
					await getScratchResults(unMappedUserResults, element, false)
						.then((calculatedResults) => {
							const c = calculatedResults[0].map((row) => {
								const r = row.map((value) => {
									if(typeof value === 'object'){
										return Array.isArray(value) ? value : value.text
									}
									return value
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				case 'freeText':
					await getFreeTextResults(unMappedUserResults, element)
						.then((calculatedResults) => {
							const c = calculatedResults[0].map((row) => {
								const r = element.tableHeader.map(({key}) => {
									return row[key];
								});
								return [...[`${currentCampaign.name} - ${element.title} ${endDate}`], ...[currentCampaignId], ...r]
							})
							const header = [...['Campaign'], ...['Campaign Id'], ...element.tableHeader.map((hItem) => { return hItem.value; })]
							c.unshift(header)
							d.push(c)
						})
					break;
				default:
					break;
			}
			return d
		})
		return d
	}, [currentCampaign, unMappedUserResults])
	const downloadCsv = useCallback(() => {
		const calculated = prepareDataForDownload(forDownload)
		Promise.all(calculated)
		.then( allCalculatedElements => {
			let d = []
			allCalculatedElements.forEach((element) => {
				element.forEach((row) => {
					d = [...d, ...row]
				})
			})
			saveCSV(d, currentCampaign.name)
		})
	}, [forDownload, currentCampaign.name, prepareDataForDownload])
	const downloadExcel = useCallback(() => {
		const calculated = prepareDataForDownload(forDownload)
		Promise.all(calculated)
		.then(allCalculatedElements => {
			let d = []
			allCalculatedElements.forEach((element) => {
				element.forEach((row) => {
					d = [...d, ...row]
				})
			})
			saveExcel(d, currentCampaign.name)
		})
	}, [forDownload])
	const isChecked = (id) => {
		return forDownload.find( element => element.id == id)
	}
	const setChecked = (element, event) => {
		if(event.target.checked){
			setForDownload([...forDownload, ...[element]])
		}else{
			setForDownload(forDownload.filter((el) => el.id != element.id))
		}
	}
	useEffect(() => {
		mapQuestions()
	}, [])
	return (
		<div>
			<div className="flex flex--row">
				{isLorealHack && 
					<table style={{marginTop: 15, marginLeft: 15}} className="space-x-2">
						<tr>
							<th>date</th>   
							<th>win</th>  
							<th>all</th>
						</tr>
						{winnerStats.map( stat => (
							<tr>
								<td>{stat.date}</td>
								<td>{stat.winnerCount}</td>
								<td>{stat.totalCount}</td>
							</tr>
						))}
					</table>
				}
				<div className="float--left">
					<button onClick={getFullDetailedReport} className="btn btn-primary btn-inverted mtop--5">
						<span className="material-icons font--25 vm--align">download</span>
						Full Detailed Report
					</button>
				</div>
				<div className="float--right">
					<Dropdown isLeft={true}>
						<div className="dropdown-trigger btn btn-primary btn-inverted download__results mtop--5" disabled={forDownload.length === 0}>
							<span className="material-icons font--25 vm--align">
								download
							</span>
							Download ({forDownload.length})
						</div>
						<div className="dropdown__menu">
							<div className="dropdown__content">
								<div className="dropdown__item" onClick={downloadCsv}>CSV</div>
								<div className="dropdown__item" onClick={downloadExcel}>Excel</div>
							</div>
						</div>
					</Dropdown>
				</div>
			</div>
			<HorizontalSlider items={sidebarItems} css={'tabs mtop--10'} itemWidth={280}>
				{hasCorrectOption && <SwiperSlide className={getCss("quizScoring")} key={`sidebarItem__-1`} onClick={() => {
					dispatch(setCurrentResultsElement(null))
					setTimeout(() => {
						dispatch(setCurrentResultsElement({id:"quizScoring", type:"quizScoring"}))
					}, 100);
				}}>
					Quiz
				</SwiperSlide>}
				{ sidebarItems.map( (item, index) => {
					return (
						<SwiperSlide className={getCss(item.id)} key={`sidebarItem__${index}`} onClick={() => {
							dispatch(setCurrentResultsElement(null))
							setTimeout(() => {
								dispatch(setCurrentResultsElement(item))	
							}, 100);
						}}>
							<div className="flex flex--row">
								<div className="material-icons font-25">{item.icon}</div>
								<span className="results__item-text">{item.title}</span>
								<div className="dropdown-trigger download__results mleft--10">
									<input type="checkbox" checked={!!isChecked(item.id)} onChange={(e) => {
										e.preventDefault();
										e.stopPropagation();
										setChecked(item, e)
									}} />
								</div>
							</div>
						</SwiperSlide>
					)
				})}
			</HorizontalSlider>
		</div>
	);
}

export default ResultsList;
